/**
 * useSipCall - Hook for direct SIP calls via LiveKit
 *
 * Manages a LiveKit Room connection for employee-direct SIP calling.
 * The backend creates a room + SIP participant and returns a token;
 * this hook connects to that room and handles audio, mute, duration, etc.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteParticipant,
  type Participant,
  ConnectionState,
} from 'livekit-client';
import api from 'app/api';
import {
  roomCallSource,
  type OutboundDirectSipCallResponse,
} from '@heltarchat/shared';
import { useSocket } from 'utils/context/SocketContext';

// ============================================================================
// Types
// ============================================================================

export type SipCallStatus =
  | 'idle'
  | 'connecting'
  | 'ringing'
  | 'connected'
  | 'disconnected'
  | 'failed';

export interface SipCallState {
  status: SipCallStatus;
  phoneNumber: string;
  isMuted: boolean;
  callDuration: number;
  roomName: string;
  /** Raw SIP call status from LiveKit participant attributes (dialing/active) */
  sipStatus: string;
}

export interface UseSipCallReturn {
  sipCallState: SipCallState;
  startCall: (phoneNumber: string) => Promise<void>;
  joinRoom: (roomName: string, phoneNumber: string) => Promise<void>;
  endCall: () => void;
  toggleMute: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const DIAL_TIMEOUT_MS = 60_000;

/** The far end of a room call: the SIP trunk participant (`sip_*`) or the
 *  web-widget visitor (`web_*`). Employees are `employee_*`. */
const isFarEnd = (identity: string): boolean =>
  identity.startsWith('sip_') || identity.startsWith('web_');
const INITIAL_STATE: SipCallState = {
  status: 'idle',
  phoneNumber: '',
  isMuted: false,
  callDuration: 0,
  roomName: '',
  sipStatus: '',
};

// ============================================================================
// Hook
// ============================================================================

export const useSipCall = (): UseSipCallReturn => {
  const [sipCallState, setSipCallState] = useState<SipCallState>(INITIAL_STATE);
  const { socket } = useSocket();

  const roomRef = useRef<Room | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const dialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track the currently active room so the socket listener can match on it
  const activeRoomNameRef = useRef<string>('');
  // Ref mirror of sipCallState.status so setTimeout callbacks read current value
  // instead of the stale closure-captured state.
  const statusRef = useRef<SipCallStatus>('idle');
  // Guard to prevent cleanup running multiple times (ParticipantDisconnected +
  // Disconnected + socket terminate can all fire within milliseconds).
  const isCleaningUpRef = useRef<boolean>(false);

  // Keep ref in sync with state (avoids stale closures in setTimeout callbacks)
  statusRef.current = sipCallState.status;

  // ------------------------------------------
  // Cleanup (idempotent — safe to call multiple times)
  // ------------------------------------------
  const cleanup = useCallback(() => {
    if (isCleaningUpRef.current) return;
    isCleaningUpRef.current = true;
    activeRoomNameRef.current = '';
    statusRef.current = 'idle';
    if (dialTimeoutRef.current) {
      clearTimeout(dialTimeoutRef.current);
      dialTimeoutRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    // Detach audio element (created by track.attach())
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.remove();
      audioRef.current = null;
    }
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    setSipCallState(INITIAL_STATE);
    // Reset guard so next call can clean up
    isCleaningUpRef.current = false;
  }, []);

  // ------------------------------------------
  // Socket listener: backend fires `receiveCall` with event=terminate on SIP
  // failure (markSipCallFailed) or on normal SIP hangup (webhookController).
  // We need this because `sipCallState` is local React state — the Redux
  // Calls slice reducers don't touch it, and the LiveKit `Disconnected` event
  // may not fire if the room connect hasn't completed yet.
  // ------------------------------------------
  useEffect(() => {
    if (!socket) return;
    const handleReceiveCall = (data: {
      event?: string;
      roomName?: string;
      failureReason?: string;
    }) => {
      // Only room calls (SIP / web) carry a roomName; WhatsApp is the SDP flow.
      if (data?.event !== 'terminate' || !data.roomName) return;
      // Every dashboard gets every hangup; only one in a call has anything to tear down.
      if (statusRef.current === 'idle') return;
      // Only act on the room we're actually in. An empty ref means we don't
      // have one yet — `startCall` is still awaiting /calls/initiate — so the
      // terminate belongs to someone else (any web visitor's hangup reaches
      // every dashboard). `joinRoom` sets the ref before its first await.
      if (data.roomName !== activeRoomNameRef.current) return;
      setSipCallState(prev => ({
        ...prev,
        status: data.failureReason ? 'failed' : 'disconnected',
      }));
      setTimeout(cleanup, data.failureReason ? 1000 : 500);
    };
    socket.on('receiveCall', handleReceiveCall);
    return () => {
      socket.off('receiveCall', handleReceiveCall);
    };
  }, [socket, cleanup]);

  // ------------------------------------------
  // Attach/detach remote audio — uses LiveKit's built-in track.attach()
  // which auto-creates an <audio> element, handles autoplay, and is
  // much simpler than manual MediaStream + createElement.
  // ------------------------------------------
  const attachRemoteAudio = useCallback((track: RemoteTrack) => {
    if (track.kind !== Track.Kind.Audio) return;
    const el = track.attach();
    el.style.display = 'none';
    audioRef.current = el;
  }, []);

  // ------------------------------------------
  // Shared room event setup — DRY helper used by both startCall and joinRoom
  // ------------------------------------------
  const setupRoomListeners = useCallback(
    (room: Room) => {
      // Far end disconnected (hangup, network fail, rejected)
      room.on(
        RoomEvent.ParticipantDisconnected,
        (participant: RemoteParticipant) => {
          if (isFarEnd(participant.identity)) {
            setSipCallState(prev => ({ ...prev, status: 'disconnected' }));
            setTimeout(cleanup, 1500);
          }
        },
      );

      // Audio track ready → attach (plays dial tone during ring, voice after answer)
      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        attachRemoteAudio(track);
      });

      // Audio track removed → detach cleanly
      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach();
          audioRef.current = null;
        }
      });

      // SIP status attribute changes (dialing → active)
      // LiveKit SIP bridge sets sip.callStatus on the SIP participant.
      // "dialing" = phone ringing on remote end, "active" = call answered.
      room.on(
        RoomEvent.ParticipantAttributesChanged,
        (
          _changedAttributes: Record<string, string>,
          participant: Participant,
        ) => {
          if (!participant.identity.startsWith('sip_')) return;
          const callStatus = participant.attributes['sip.callStatus'] || '';
          setSipCallState(prev => {
            if (callStatus === 'active' && prev.status !== 'connected') {
              return { ...prev, sipStatus: callStatus, status: 'connected' };
            }
            return { ...prev, sipStatus: callStatus };
          });
        },
      );

      // Room disconnected (network loss on employee side)
      room.on(RoomEvent.Disconnected, () => {
        setSipCallState(prev => {
          if (prev.status === 'idle') return prev;
          return { ...prev, status: 'disconnected' };
        });
        setTimeout(cleanup, 1500);
      });
    },
    [cleanup, attachRemoteAudio],
  );

  // ------------------------------------------
  // Start a direct SIP call
  // ------------------------------------------
  const startCall = useCallback(
    async (phoneNumber: string) => {
      if (statusRef.current !== 'idle') return;

      setSipCallState(prev => ({
        ...prev,
        status: 'connecting',
        phoneNumber,
      }));

      try {
        // 1. Backend: create room + SIP participant + employee token
        const response = await api.post<{
          data: OutboundDirectSipCallResponse;
        }>('/calls/initiate', {
          clientWaNumber: phoneNumber,
          callType: 'sip',
          mode: 'direct',
        });

        const { roomName, token, livekitUrl } = response.data.data;
        activeRoomNameRef.current = roomName;

        // 2. Create LiveKit room + register event listeners
        const room = new Room();
        roomRef.current = room;

        // Helper: transition to connected + start duration timer (guarded
        // against double-start from ParticipantConnected + post-connect check)
        const markConnected = () => {
          if (dialTimeoutRef.current) {
            clearTimeout(dialTimeoutRef.current);
            dialTimeoutRef.current = null;
          }
          setSipCallState(prev => ({
            ...prev,
            status: 'connected',
            roomName,
          }));
          if (!durationIntervalRef.current) {
            durationIntervalRef.current = setInterval(() => {
              setSipCallState(prev => ({
                ...prev,
                callDuration: prev.callDuration + 1,
              }));
            }, 1000);
          }
        };

        // SIP callee answered → connected state + duration timer
        room.on(
          RoomEvent.ParticipantConnected,
          (participant: RemoteParticipant) => {
            if (isFarEnd(participant.identity)) markConnected();
          },
        );

        setupRoomListeners(room);

        // 3. Connect + unblock browser autoplay + enable mic
        await room.connect(livekitUrl, token);
        await room.startAudio();
        await room.localParticipant.setMicrophoneEnabled(true);

        setSipCallState(prev => ({ ...prev, roomName }));

        // Check if SIP participant already in room (may have connected before us)
        for (const [, participant] of room.remoteParticipants) {
          if (isFarEnd(participant.identity)) {
            markConnected();
            break;
          }
        }

        // Dial timeout — auto-disconnect if no answer within 60s.
        // Uses statusRef (not sipCallState) to avoid stale closure.
        dialTimeoutRef.current = setTimeout(() => {
          if (
            roomRef.current?.state === ConnectionState.Connected &&
            statusRef.current !== 'connected'
          ) {
            cleanup();
          }
        }, DIAL_TIMEOUT_MS);
      } catch (error) {
        console.error('SIP startCall failed:', error);
        setSipCallState(prev => ({ ...prev, status: 'failed' }));
        setTimeout(cleanup, 2000);
      }
    },
    [cleanup, setupRoomListeners],
  );

  // ------------------------------------------
  // Join existing room (for accepting inbound SIP calls)
  // ------------------------------------------
  const joinRoom = useCallback(
    async (roomName: string, phoneNumber: string) => {
      if (statusRef.current !== 'idle') return;

      setSipCallState(prev => ({
        ...prev,
        status: 'connecting',
        phoneNumber,
        roomName,
      }));
      activeRoomNameRef.current = roomName;

      try {
        // 1. Call backend to get token for the existing room
        const response = await api.post<{
          data: { roomName: string; token: string; livekitUrl: string };
        }>('/calls/accept', {
          callType: roomCallSource(roomName),
          roomName,
        });

        const { token, livekitUrl } = response.data.data;

        // The caller may have hung up while we were accepting (terminate
        // arrived → cleanup ran) — joining now would connect to a dead room.
        const stillAccepting = (): boolean =>
          statusRef.current === 'connecting' &&
          activeRoomNameRef.current === roomName;
        if (!stillAccepting()) return;

        // 2. Create LiveKit room and connect
        const room = new Room();
        roomRef.current = room;

        // Shared listeners (disconnect, track subscribe/unsubscribe, room disconnect)
        setupRoomListeners(room);

        // 3. Connect to the room
        await room.connect(livekitUrl, token);
        if (!stillAccepting()) {
          await room.disconnect();
          return;
        }
        await room.startAudio();

        // 4. Enable microphone
        await room.localParticipant.setMicrophoneEnabled(true);

        // Already connected (SIP participant is in the room) — start timer
        setSipCallState(prev => ({
          ...prev,
          status: 'connected',
          roomName,
        }));

        durationIntervalRef.current = setInterval(() => {
          setSipCallState(prev => ({
            ...prev,
            callDuration: prev.callDuration + 1,
          }));
        }, 1000);
      } catch (error) {
        console.error('SIP join room failed:', error);
        setSipCallState(prev => ({ ...prev, status: 'failed' }));
        setTimeout(cleanup, 2000);
      }
    },
    [cleanup, setupRoomListeners],
  );

  // ------------------------------------------
  // End call — notify backend to destroy room + SIP participant immediately,
  // then clean up local state. Without the API call, the SIP trunk stays
  // alive for up to emptyTimeout (5 min) and the caller hears dead air.
  // ------------------------------------------
  const endCall = useCallback(() => {
    const roomName = activeRoomNameRef.current;
    if (roomName) {
      api
        .post('/calls/terminate', {
          callType: roomCallSource(roomName),
          roomName,
        })
        .catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  // ------------------------------------------
  // Toggle mute
  // ------------------------------------------
  const toggleMute = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;

    const newMuted = room.localParticipant.isMicrophoneEnabled;
    room.localParticipant.setMicrophoneEnabled(!newMuted);
    setSipCallState(prev => ({ ...prev, isMuted: newMuted }));
  }, []);

  // ------------------------------------------
  // Cleanup on unmount
  // ------------------------------------------
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return { sipCallState, startCall, joinRoom, endCall, toggleMute };
};
